import * as React from 'react';
import { useState, useEffect } from 'react';

import { ISearchFormProps, IYado, IResort, IYadoPagination, IYadoLowestPrice, ICurrency, INavArray } from './types';
import { wait, countPeople, countNights, scrollTo, defaultRooms, stringifyParams, fetchYadoListPrices } from './helpers';
import { formatDate } from '../utils/dateHelper';
import YadoItem from './YadoItem';
import YadoListTitle from './YadoListTitle';
import YadoItemMobile from './YadoItemMobile';
import YadoPaginationButton from './YadoPaginationButton';
import ResortItem from './ResortItem';
import SnowSearchFormModal from './SnowSearchFormModal';
import YadoSearchForm from './YadoSearchForm';
import PillNavs from './PillNavs';
import { SNOW_SEARCH_FIELD_FOCUSED, SNOW_SEARCH_MODAL_TRIGGERED } from './events';

interface IProps extends ISearchFormProps {
  yados: IYado[];
  resorts: IResort[];
  currency: ICurrency;
  isMobile: boolean;
}

const YADO_COUNT_PER_PAGE = 10;

function sliceYados(yados: IYado[], page: number): IYado[] {
  return yados.slice(0, YADO_COUNT_PER_PAGE * page);
}

function mergeYados(prevYados: IYado[], nextYados: IYado[]): IYado[] {
  // 同じIDであれば既存オブジェクトを上書き。異なれば配列に追加。
  const existingYados = [...prevYados];
  const newYados = [...nextYados];
  existingYados.forEach(existing => {
    const sameYadoIndex = newYados.findIndex(newYado => newYado.id === existing.id);
    if (sameYadoIndex !== -1) {
      Object.assign(existing, newYados[sameYadoIndex]);
      newYados.splice(sameYadoIndex, 1);
    }
  });
  return [...existingYados, ...newYados];
}

function paginate(yados: IYado[], page: number): IYadoPagination {
  const hasNext = yados.length > page * YADO_COUNT_PER_PAGE;
  const hasPrev = page > 1;
  return { page, hasNext, hasPrev };
}


const YadoList: React.FC<IProps> = (props) => {
  const { currency, yados, resorts, checkin_date, checkout_date, rooms, isMobile } = props;

  const [pagination, setPagination] = useState<IYadoPagination>(paginate(yados, 1));
  const [currentYados, setCurrentYados] = useState<IYado[]>(sliceYados(yados, 1));
  const [isLoading, setLoading] = useState<boolean>(false);

  const isPriceSearchable = checkin_date && checkout_date;

  async function handleNextClick() {
    if (pagination.hasNext) {
      setLoading(true);
      await wait();
      setPagination(paginate(yados, pagination.page + 1));
      setLoading(false);
    }
  }

  function yadoUri(yado: IYado): string {
    return [
      `/snow/accommodations/${yado.id}`,
      '?',
      stringifyParams({
        checkin_date: checkin_date ? formatDate(checkin_date) : null,
        checkout_date: checkout_date ? formatDate(checkout_date) : null,
        rooms: defaultRooms(rooms)
      }),
    ].join('');
  }

  function handleYadoItemClick(event: React.MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (isMobile) {
      document.dispatchEvent(new CustomEvent(SNOW_SEARCH_MODAL_TRIGGERED));
      document.dispatchEvent(new CustomEvent(
        SNOW_SEARCH_FIELD_FOCUSED, { detail: { focusedField: 'date', placement: 'modal' } }));
    } else {
      document.dispatchEvent(new CustomEvent(
        SNOW_SEARCH_FIELD_FOCUSED, { detail: { focusedField: 'date', placement: 'header' } }));
      scrollTo(document.documentElement, 0, 300);
    }
  }

  useEffect(() => {
    const prevYados = [...currentYados];
    const diffYados = [...yados].splice((pagination.page - 1) * YADO_COUNT_PER_PAGE, YADO_COUNT_PER_PAGE)
                                .map(yado => ({ ...yado, isPriceLoaded: isPriceSearchable ? false : undefined }));
    setCurrentYados(mergeYados(prevYados, diffYados));

    if (!isPriceSearchable) { return; }
    const params = {
      checkin_date: formatDate(checkin_date),
      checkout_date: formatDate(checkout_date),
      rooms: defaultRooms(rooms),
      yado_ids: diffYados.filter(yado => yado.isPriceLoaded !== true).map(yado => yado.id),
    };
    fetchYadoListPrices(params, (response: { yados: IYadoLowestPrice[] }) => {
      const yadoPrices = response.yados;
      if (yadoPrices != null) {
        const newYados = diffYados.map((yado) => {
          if (yado.isPriceLoaded) { return yado; }
          const priceData = yadoPrices.find(price => price.id === yado.id);
          return { ...yado, isPriceLoaded: true, lowestPrice: priceData ? priceData.lowestPrice : null };
        });
        setCurrentYados(mergeYados(prevYados, newYados));
      }
    });
  }, [pagination]);

  const navArray: INavArray = {
    item: ['lift', 'rental', 'lesson', 'transport'],
    insurance: ['insurance'],
  };

  function renderForDesktop() {
    return (
      <React.Fragment>
        <div style={{ padding: '0.5em 0.5em 0 0.5em', backgroundColor: 'var(--gray300)' }}>
          <YadoSearchForm {...props} placement="header" isMobile={false} is_toppage={false} />
        </div>
        <div style={{ width: 1140, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', margin: '1em 0' }}>
            <PillNavs navArray={navArray} q={props.q} />
            <YadoListTitle q={props.q} count={yados.length} />
          </div>
          <div style={{ display: 'flex' }}>
            <div style={{ flex: 1, marginRight: 24 }}>
              <div style={{ position: 'sticky', top: 56 }}>
                {resorts.map(resort => <ResortItem key={resort.code} currency={currency} resort={resort} />)}
              </div>
            </div>
            <div style={{ minWidth: 800 }}>
              {currentYados.map(yado => (
                <YadoItem
                  key={yado.id}
                  currency={currency}
                  yado={yado}
                  uri={yadoUri(yado)}
                  handleClick={isPriceSearchable ? null : handleYadoItemClick}
                  numberOfNights={countNights(checkin_date, checkout_date)}
                  numberOfPeople={countPeople(rooms).total} />
              ))}
              <YadoPaginationButton
                pagination={pagination}
                isLoading={isLoading}
                handleNextClick={handleNextClick} />
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  function renderForMobile() {
    return (
      <div style={{ overflowX: 'hidden', padding: '0 1em' }}>
        <SnowSearchFormModal {...props} triggerType="yadoField" activePill="yado" />
        <div style={{ margin: '0 -1em 1em 0', padding: '0.5em 0 1em', overflowX: 'scroll' }}>
          <PillNavs navArray={navArray} q={props.q} />
        </div>
        <div>
          {currentYados.map(yado => (
            <YadoItemMobile
              key={yado.id}
              currency={currency}
              yado={yado}
              uri={yadoUri(yado)}
              handleClick={isPriceSearchable ? null : handleYadoItemClick}
              numberOfNights={countNights(checkin_date, checkout_date)}
              numberOfPeople={countPeople(rooms).total} />
          ))}
          <YadoPaginationButton
            pagination={pagination}
            isLoading={isLoading}
            handleNextClick={handleNextClick} />
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      {isMobile ? renderForMobile() : renderForDesktop()}
    </React.Fragment>
  );
}

export default YadoList;
